"use client";

import { useCallback, useMemo } from "react";
import { create } from "zustand";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import type { StreetViewLayer } from "@/types";

interface StreetViewStore {
  /**
   * Null while the user hasn't touched the switch and the project's own default
   * still applies — the same distinction the secondary layers make between "not
   * chosen" and "chosen to be off".
   */
  active: boolean | null;
  setActive: (active: boolean) => void;
}

export const useStreetViewStore = create<StreetViewStore>((set) => ({
  active: null,
  setActive: (active) => set({ active }),
}));

/**
 * Groups that carry at least one layer.
 *
 * Tenants routinely have an empty street-view group left over from the CMS
 * template. Legacy counted groups, so those projects got a switch that flicked
 * back and forth over nothing.
 */
export function toggleableGroups(
  groups: StreetViewLayer[] | undefined
): StreetViewLayer[] {
  return (groups ?? []).filter((group) => (group.layers?.length ?? 0) > 0);
}

/**
 * Whether street view starts visible.
 *
 * Note the field reads backwards from everywhere else: for a *secondary* layer
 * `disabled` means "starts off", while here legacy treated a truthy `disabled` on
 * the first group as "starts on" and everything else as off. That is preserved
 * rather than corrected — reading it the other way would switch street view on by
 * default for every tenant that has it, since almost none set the field at all.
 */
export function defaultActive(groups: StreetViewLayer[] | undefined): boolean {
  return !!groups?.[0]?.disabled;
}

/** The street-view layers and the single switch that shows or hides them. */
export function useStreetView() {
  const { streetViewLayers } = useProjectConfigs();
  const stored = useStreetViewStore((state) => state.active);
  const setActive = useStreetViewStore((state) => state.setActive);

  const groups = useMemo(
    () => toggleableGroups(streetViewLayers),
    [streetViewLayers]
  );

  const isActive = stored ?? defaultActive(streetViewLayers);

  return {
    groups,
    isAvailable: groups.length > 0,
    isActive,
    toggle: useCallback(() => setActive(!isActive), [isActive, setActive]),
  };
}
